import { useEffect } from 'react';
import { DeckNavBar } from './components';
import { useDeckNavigation } from './hooks';
import './ContentRewardsPitchDeck.css';

// Set document title for the deck
const DECK_TITLE = 'Content Rewards | o37';
const TOTAL_SLIDES = 15;

// Image URLs
const CONTENT_REWARDS_LOGO = 'https://jlqgwkkdzpadbmnyqnnk.supabase.co/storage/v1/object/public/public_bucket/vdr_contentrewards/contentrewards_logo.avif';
const DANIEL_BITTON_PFP = 'https://jlqgwkkdzpadbmnyqnnk.supabase.co/storage/v1/object/public/public_bucket/vdr_contentrewards/danielbitton_pfp.jpg';
const AIRRACK_PFP = 'https://jlqgwkkdzpadbmnyqnnk.supabase.co/storage/v1/object/public/public_bucket/vdr_contentrewards/airrack_pfp.jpg';

export default function ContentRewardsPitchDeck() {
  const {
    currentSlide,
    nextSlide,
    prevSlide,
    progress,
    isFirstSlide,
    isLastSlide,
  } = useDeckNavigation({ totalSlides: TOTAL_SLIDES });

  // Set document title
  useEffect(() => {
    const previousTitle = document.title;
    document.title = DECK_TITLE;
    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <div className="pitch-deck">
      <div className="deck-container">
        {/* Navigation Bar */}
        <DeckNavBar
          logoUrl={CONTENT_REWARDS_LOGO}
          logoText="CONTENT REWARDS"
          currentSlide={currentSlide}
          totalSlides={TOTAL_SLIDES}
          variant="default"
        />

        {/* Slides */}
        <div
          className="slides-wrapper"
          style={{ transform: `translateX(-${currentSlide * 100}vw)` }}
        >
          {/* Slide 1: Title */}
          <div className="slide hero">
            <div className="raise-badge">
              <span className="dot"></span>
              Fundraise - January 2026
            </div>
            <h1 className="slide-title">
              The <span className="highlight">Meta Ads</span><br />for Organic Content
            </h1>
            <p className="slide-subtitle">
              Platform that turns organic content into a programmatic ad product. Connects brands with creators for performance-based campaigns across TikTok, YouTube, Instagram, and X.
            </p>
            <div className="hero-stats">
              <div className="hero-stat">
                <span className="hero-stat-value">3.7B+</span>
                <span className="hero-stat-label">Views Generated</span>
              </div>
              <div className="hero-stat">
                <span className="hero-stat-value">105K+</span>
                <span className="hero-stat-label">Creators</span>
              </div>
              <div className="hero-stat">
                <span className="hero-stat-value accent">408%</span>
                <span className="hero-stat-label">MoM Growth</span>
              </div>
              <div className="hero-stat">
                <span className="hero-stat-value">447</span>
                <span className="hero-stat-label">Agencies</span>
              </div>
            </div>
          </div>

          {/* Slide 2: Personal Backstory */}
          <div className="slide">
            <span className="slide-label">Founder Journey</span>
            <h2 className="slide-title">Building in the Content Space<br /><span className="highlight">My Entire Life</span></h2>
            <p className="slide-subtitle">
              A decade of building, scaling, and monetizing content-from YouTube videos at age 8 to $7M in revenue at 16.
            </p>

            <div className="timeline-horizontal">
              <div className="timeline-item-h">
                <div className="timeline-dot">8</div>
                <div className="timeline-content-h">
                  <div className="timeline-title-h">YouTube</div>
                  <div className="timeline-desc-h">Started experimenting with videos</div>
                </div>
              </div>
              <div className="timeline-item-h">
                <div className="timeline-dot">13</div>
                <div className="timeline-content-h">
                  <div className="timeline-title-h">$3-4K/mo</div>
                  <div className="timeline-desc-h">Video editing for major YouTubers</div>
                </div>
              </div>
              <div className="timeline-item-h">
                <div className="timeline-dot">14</div>
                <div className="timeline-content-h">
                  <div className="timeline-title-h">1M Subs</div>
                  <div className="timeline-desc-h">Built channel in 6 months</div>
                </div>
              </div>
              <div className="timeline-item-h">
                <div className="timeline-dot">15</div>
                <div className="timeline-content-h">
                  <div className="timeline-title-h">$500K+</div>
                  <div className="timeline-desc-h">Snapchat shows first month (then lost it all)</div>
                </div>
              </div>
              <div className="timeline-item-h">
                <div className="timeline-dot">16</div>
                <div className="timeline-content-h">
                  <div className="timeline-title-h">Crayo.ai</div>
                  <div className="timeline-desc-h">$7M revenue, $0 ad spend, $0 funding</div>
                </div>
              </div>
              <div className="timeline-item-h">
                <div className="timeline-dot">17</div>
                <div className="timeline-content-h">
                  <div className="timeline-title-h">$10-24M</div>
                  <div className="timeline-desc-h">Acquisition offers declined</div>
                </div>
              </div>
            </div>

            <div className="founder-highlight">
              <div className="founder-avatar">
                <img src={DANIEL_BITTON_PFP} alt="Daniel Bitton" />
              </div>
              <div className="founder-info">
                <h3>Daniel Bitton</h3>
                <div className="founder-role">CEO & Founder - 18 years old</div>
              </div>
            </div>
          </div>

          {/* Slide 3: Problem */}
          <div className="slide">
            <span className="slide-label">The Problem</span>
            <h2 className="slide-title">Influencer Marketing<br />is <span className="highlight">Broken</span></h2>
            <p className="slide-subtitle">
              Brands forced to choose: guaranteed attention (Ads) OR authentic reach (Influencers). No middle ground exists.
            </p>

            <div className="stat-highlight">
              <div className="stat-box">
                <div className="stat-box-value">28%</div>
                <div className="stat-box-label">of brands say ROI measurement is their #1 challenge</div>
              </div>
              <div className="stat-box">
                <div className="stat-box-value">$1.3B</div>
                <div className="stat-box-label">lost to influencer fraud annually</div>
              </div>
              <div className="stat-box">
                <div className="stat-box-value">60%</div>
                <div className="stat-box-label">of brands have dealt with influencer fraud</div>
              </div>
              <div className="stat-box">
                <div className="stat-box-value">~15%</div>
                <div className="stat-box-label">of all influencer spend is wasted</div>
              </div>
            </div>

            <div className="cards-grid two-col-cards">
              <div className="card">
                <div className="card-icon">&#128184;</div>
                <h3 className="card-title">Opaque Flat-Fee Deals</h3>
                <p className="card-text">No performance accountability. Brands pay upfront with zero guarantee of results.</p>
              </div>
              <div className="card">
                <div className="card-icon">&#128034;</div>
                <h3 className="card-title">Too Slow for Events</h3>
                <p className="card-text">Not built for quickly promoting big events or time-sensitive campaigns.</p>
              </div>
              <div className="card">
                <div className="card-icon">&#128100;</div>
                <h3 className="card-title">Small Creators Locked Out</h3>
                <p className="card-text">Hard for talented smaller creators to actually monetize their audience.</p>
              </div>
              <div className="card">
                <div className="card-icon">&#9888;&#65039;</div>
                <h3 className="card-title">Unscalable Agency Model</h3>
                <p className="card-text">Manual processes, high fees, slow turnarounds. Breaks down at scale.</p>
              </div>
            </div>
          </div>

          {/* Slide 4: Market Overview */}
          <div className="slide">
            <span className="slide-label">Market Opportunity</span>
            <h2 className="slide-title">Massive Market.<br /><span className="highlight">Perfect Timing.</span></h2>
            <p className="slide-subtitle">
              The gap: programmatic infrastructure exists for paid ads-nothing comparable for organic content. Until now.
            </p>

            <div className="market-grid">
              <div className="market-card">
                <div className="market-card-label">Creator Economy (Goldman Sachs)</div>
                <div className="market-card-row">
                  <div className="market-value">$250B</div>
                  <div className="market-arrow">&#8594;</div>
                  <div className="market-value accent">$480B</div>
                </div>
                <div className="market-subtext">2025 &#8594; 2027 - 22%+ CAGR</div>
              </div>
              <div className="market-card">
                <div className="market-card-label">Influencer Marketing</div>
                <div className="market-card-row">
                  <div className="market-value">$32B</div>
                  <div className="market-arrow">&#8594;</div>
                  <div className="market-value accent">$48B</div>
                </div>
                <div className="market-subtext">2025 &#8594; 2027</div>
              </div>
            </div>

            <div className="market-stats-row">
              <div className="market-mini-stat">
                <div className="market-mini-value">200M+</div>
                <div className="market-mini-label">Content creators worldwide</div>
              </div>
              <div className="market-mini-stat">
                <div className="market-mini-value">50M+</div>
                <div className="market-mini-label">Actively monetizing</div>
              </div>
              <div className="market-mini-stat">
                <div className="market-mini-value">86%</div>
                <div className="market-mini-label">Of marketers use influencer marketing</div>
              </div>
            </div>
          </div>

          {/* Slide 5: Current Solutions */}
          <div className="slide">
            <span className="slide-label">Competitive Landscape</span>
            <h2 className="slide-title">Others Entered the Space.<br /><span className="highlight">None Are Enterprise-Ready.</span></h2>
            <p className="slide-subtitle">
              Other platforms have entered this space-none have built the full infrastructure layer required for enterprise scale.
            </p>

            <div className="competitor-table-container">
              <table className="competitor-table">
                <thead>
                  <tr>
                    <th>Platform</th>
                    <th>Key Limitation</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="comp-name">SideShift</td>
                    <td className="comp-limitation">No audience demographic tracking</td>
                  </tr>
                  <tr>
                    <td className="comp-name">Vyro</td>
                    <td className="comp-limitation">No bot detection, no performance payouts</td>
                  </tr>
                  <tr>
                    <td className="comp-name">Payper</td>
                    <td className="comp-limitation">No approvals, no analytics</td>
                  </tr>
                  <tr>
                    <td className="comp-name">ClippingEXE</td>
                    <td className="comp-limitation">No sound detection, limited analytics</td>
                  </tr>
                  <tr>
                    <td className="comp-name">Clip.tech</td>
                    <td className="comp-limitation">No analytics</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="differentiator-box" style={{ marginTop: '32px' }}>
              <div className="differentiator-label">Content Rewards is the only enterprise-ready platform</div>
              <div className="differentiator-grid">
                <div className="diff-item">
                  <div className="diff-icon">&#128737;&#65039;</div>
                  <div className="diff-title">Anti-Fraud Infrastructure</div>
                  <div className="diff-desc">Multi-layer bot detection with <strong>sound detection</strong> (unique in market)</div>
                </div>
                <div className="diff-item">
                  <div className="diff-icon">&#9881;&#65039;</div>
                  <div className="diff-title">Programmatic Tooling</div>
                  <div className="diff-desc">Full analytics, self-serve controls, complete API access</div>
                </div>
                <div className="diff-item">
                  <div className="diff-icon">&#9878;&#65039;</div>
                  <div className="diff-title">Neutral Positioning</div>
                  <div className="diff-desc">Platform protocol vs. creator-led portfolios</div>
                </div>
              </div>
            </div>
          </div>

          {/* Slide 6: The Solution */}
          <div className="slide">
            <span className="slide-label">The Solution</span>
            <h2 className="slide-title">Programmatic Infrastructure<br />for <span className="highlight">Organic Content</span></h2>
            <p className="slide-subtitle">
              Performance-based marketplace with transparent CPM pricing, automated payouts, and enterprise-grade fraud detection.
            </p>
            <div className="cards-grid">
              <div className="card">
                <div className="card-icon">&#128181;</div>
                <h3 className="card-title">Transparent CPM Pricing</h3>
                <p className="card-text">Brands pay for performance. Creators earn based on actual verified views.</p>
              </div>
              <div className="card">
                <div className="card-icon">&#129302;</div>
                <h3 className="card-title">Automated Payouts</h3>
                <p className="card-text">Zero-touch operations. Infrastructure cost: $0.18 per $35 payout (~0.5%).</p>
              </div>
              <div className="card">
                <div className="card-icon">&#128737;&#65039;</div>
                <h3 className="card-title">Enterprise Fraud Detection</h3>
                <p className="card-text">Multi-signal bot detection with 90% accuracy, targeting 95%+.</p>
              </div>
              <div className="card">
                <div className="card-icon">&#128200;</div>
                <h3 className="card-title">Real-Time Analytics</h3>
                <p className="card-text">Programmatic dashboards with live performance tracking and ROI attribution.</p>
              </div>
              <div className="card">
                <div className="card-icon">&#128268;</div>
                <h3 className="card-title">Self-Serve + Full API</h3>
                <p className="card-text">Brands and agencies run campaigns at any scale without human intervention.</p>
              </div>
              <div className="card">
                <div className="card-icon">&#9986;&#65039;</div>
                <h3 className="card-title">"Clipping" Mechanism</h3>
                <p className="card-text">Creators transform long-form IP into viral short-form assets at scale.</p>
              </div>
            </div>
          </div>

          {/* Slide 7: What's Happening Now */}
          <div className="slide">
            <span className="slide-label">Why Now</span>
            <h2 className="slide-title">The Whop Migration<br /><span className="highlight">Changes Everything</span></h2>
            <p className="slide-subtitle">
              December 21, 2025: Whop completed full migration-redirecting all traffic to Content Rewards. Instant scale.
            </p>
            <div className="cards-grid">
              <div className="card">
                <div className="card-icon">&#128640;</div>
                <h3 className="card-title">$1M+/mo GMV Absorbed</h3>
                <p className="card-text">Validated, live GMV from 900-1,200 active campaigns. Zero customer churn.</p>
              </div>
              <div className="card">
                <div className="card-icon">&#128101;</div>
                <h3 className="card-title">12M Monthly Visitors</h3>
                <p className="card-text">Prominent placement on Whop Discover page now routing to us.</p>
              </div>
              <div className="card">
                <div className="card-icon">&#128269;</div>
                <h3 className="card-title">Top-3 Search Term</h3>
                <p className="card-text">"Clipping" ranks as one of the most searched terms on Whop.</p>
              </div>
            </div>

            <div className="differentiator-box" style={{ marginTop: '32px' }}>
              <div className="differentiator-text">
                This is the <span className="highlight">first infrastructure layer</span> for organic content-and it's Content Rewards.
              </div>
            </div>

            <div className="info-box">
              <div className="info-box-title">Transfer Terms</div>
              <div className="info-box-content">
                <div>&#10003; All IP rights transferred</div>
                <div>&#10003; Brand assets included</div>
                <div>&#10003; User migration rights</div>
                <div>&#10003; Whop retained <strong style={{ color: 'var(--accent)' }}>zero equity</strong></div>
              </div>
            </div>
          </div>

          {/* Slide 8: Traction */}
          <div className="slide">
            <span className="slide-label">Traction - Oct-Dec 2025</span>
            <h2 className="slide-title">Proof of Execution.<br /><span className="highlight">Zero Marketing Spend.</span></h2>
            <p className="slide-subtitle">
              Pure organic growth driven by creator word-of-mouth. Operationally profitable from day one.
            </p>
            <div className="metrics-grid">
              <div className="metric-card">
                <div className="metric-value accent">105K+</div>
                <div className="metric-label">Creator Signups</div>
              </div>
              <div className="metric-card">
                <div className="metric-value">3.7B</div>
                <div className="metric-label">Views Generated</div>
              </div>
              <div className="metric-card">
                <div className="metric-value">$1.2M+</div>
                <div className="metric-label">Brand Deposits</div>
              </div>
              <div className="metric-card">
                <div className="metric-value">$800K+</div>
                <div className="metric-label">Creator Payouts</div>
              </div>
              <div className="metric-card">
                <div className="metric-value accent">408%</div>
                <div className="metric-label">MoM Revenue Growth</div>
              </div>
              <div className="metric-card">
                <div className="metric-value">571</div>
                <div className="metric-label">Campaigns Run</div>
              </div>
              <div className="metric-card">
                <div className="metric-value">447</div>
                <div className="metric-label">Agency Accounts</div>
              </div>
              <div className="metric-card">
                <div className="metric-value accent">~$85K</div>
                <div className="metric-label">Monthly Revenue</div>
              </div>
            </div>
          </div>

          {/* Slide 9: Growth Momentum */}
          <div className="slide">
            <span className="slide-label">Growth Momentum</span>
            <h2 className="slide-title">Explosive Growth.<br /><span className="highlight">Slowest Ad Month.</span></h2>
            <p className="slide-subtitle">
              January is historically the slowest month for advertising. We're seeing record numbers.
            </p>
            <div className="metrics-grid">
              <div className="metric-card">
                <div className="metric-value accent">$28K</div>
                <div className="metric-label">Daily GMV Run Rate</div>
              </div>
              <div className="metric-card">
                <div className="metric-value">$40K+</div>
                <div className="metric-label">Peak Day GMV</div>
              </div>
              <div className="metric-card">
                <div className="metric-value accent">2.3x</div>
                <div className="metric-label">Growth in 12 Days</div>
              </div>
              <div className="metric-card">
                <div className="metric-value">~$850K</div>
                <div className="metric-label">Monthly GMV Projection</div>
              </div>
            </div>

            <div className="differentiator-box" style={{ marginTop: '32px' }}>
              <div className="differentiator-text">
                This is <span className="highlight">January</span> — the slowest month in advertising
              </div>
            </div>
          </div>

          {/* Slide 10: Strategic Partnership */}
          <div className="slide">
            <span className="slide-label">Strategic Partnership</span>
            <h2 className="slide-title">Airrack as<br /><span className="highlight">Distribution Moat</span></h2>
            <p className="slide-subtitle">
              Equity partner with 50M+ followers driving creator acquisition and brand credibility at scale.
            </p>

            <div className="partner-showcase">
              <div className="partner-header">
                <div className="partner-avatar-lg">
                  <img src={AIRRACK_PFP} alt="Airrack" />
                </div>
                <div>
                  <div className="partner-name-lg">Airrack (Eric Decker)</div>
                  <div className="partner-handle">Strategic Partner & Equity Holder</div>
                </div>
              </div>

              <div className="partner-stats-grid">
                <div className="partner-stat-card">
                  <div className="partner-stat-value">50M+</div>
                  <div className="partner-stat-label">Total Followers</div>
                </div>
                <div className="partner-stat-card">
                  <div className="partner-stat-value">17.5M</div>
                  <div className="partner-stat-label">YouTube Subscribers</div>
                </div>
                <div className="partner-stat-card">
                  <div className="partner-stat-value">140K+</div>
                  <div className="partner-stat-label">Users Driven via Video</div>
                </div>
                <div className="partner-stat-card">
                  <div className="partner-stat-value">$5M</div>
                  <div className="partner-stat-label">His Agency's Recent Raise</div>
                </div>
              </div>

              <div className="partner-details">
                <div className="partner-detail-box">
                  <div className="partner-detail-title">Equity Structure</div>
                  <div className="partner-detail-value">
                    <strong>2% vesting</strong> + <strong>6% performance-based</strong><br />
                    Performance tied to $45M closed deal flow
                  </div>
                </div>
                <div className="partner-detail-box">
                  <div className="partner-detail-title">Exclusive Partnership</div>
                  <div className="partner-detail-value">
                    His agency runs <strong>exclusively on Content Rewards</strong><br />
                    Just raised $5M at $25M valuation
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Slide 11: Case Studies */}
          <div className="slide">
            <span className="slide-label">Case Studies</span>
            <h2 className="slide-title">Enterprise Brands.<br /><span className="highlight">Real Results.</span></h2>
            <p className="slide-subtitle">
              Delivering performance across entertainment, technology, luxury, and live events.
            </p>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Vertical</th>
                    <th>Views</th>
                    <th>Spend</th>
                    <th>CPM</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><span className="client-name">Druski</span></td>
                    <td><span className="tag">Entertainment</span></td>
                    <td>680M</td>
                    <td>$340K</td>
                    <td><span className="cpm-value">$0.50</span></td>
                  </tr>
                  <tr>
                    <td><span className="client-name">Polymarket</span></td>
                    <td><span className="tag">Technology</span></td>
                    <td>164M</td>
                    <td>$9.5K</td>
                    <td><span className="cpm-value">$0.08</span></td>
                  </tr>
                  <tr>
                    <td><span className="client-name">Jacob & Co</span></td>
                    <td><span className="tag">Luxury</span></td>
                    <td>26.8M</td>
                    <td>$26.8K</td>
                    <td><span className="cpm-value">$1.00</span></td>
                  </tr>
                  <tr>
                    <td><span className="client-name">F1 Miami GP</span></td>
                    <td><span className="tag">Live Events</span></td>
                    <td>3.2M</td>
                    <td>$16K</td>
                    <td><span className="cpm-value">$5.00</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="clients-list">
              <span className="client-tag">F1</span>
              <span className="client-tag">Druski</span>
              <span className="client-tag">Jacob & Co</span>
              <span className="client-tag">Stake.com</span>
              <span className="client-tag">Polymarket</span>
              <span className="client-tag">Eleven Labs</span>
              <span className="client-tag">Lil Uzi Vert</span>
            </div>
          </div>

          {/* Slide 12: Vision */}
          <div className="slide">
            <span className="slide-label">Vision</span>
            <h2 className="slide-title">Building the Operating System<br />for <span className="highlight">Organic Content</span></h2>
            <p className="slide-subtitle">
              Capturing the programmatic layer of organic spend in a $32B+ market growing to $48B by 2027.
            </p>

            <div className="vision-grid">
              <div className="vision-card featured">
                <div className="vision-icon">&#127919;</div>
                <div className="vision-title">Meta Ads for Organic - Category Dominance</div>
                <div className="vision-text">Just as Meta built the infrastructure for paid social, we're building the infrastructure for organic. The operating system that powers the entire creator economy.</div>
              </div>
              <div className="vision-card">
                <div className="vision-icon">&#128202;</div>
                <div className="vision-title">Beyond CPM</div>
                <div className="vision-text">Retainer management, pay-per-video, affiliate campaigns-multiple monetization models in one platform.</div>
              </div>
              <div className="vision-card">
                <div className="vision-icon">&#128101;</div>
                <div className="vision-title">Full Demographics</div>
                <div className="vision-text">Complete audience tracking and targeting. Know exactly who's seeing your content.</div>
              </div>
              <div className="vision-card">
                <div className="vision-icon">&#128279;</div>
                <div className="vision-title">Attribution Engine</div>
                <div className="vision-text">End-to-end tracking from view to conversion. True ROI measurement.</div>
              </div>
              <div className="vision-card">
                <div className="vision-icon">&#128269;</div>
                <div className="vision-title">Discovery Hub</div>
                <div className="vision-text">AI-powered creator matching. Right creator, right brand, right campaign.</div>
              </div>
            </div>
          </div>

          {/* Slide 13: Data Advantage */}
          <div className="slide">
            <span className="slide-label">Data Advantage</span>
            <h2 className="slide-title">The Data Layer<br /><span className="highlight">Is the Real Moat</span></h2>
            <p className="slide-subtitle">
              No competitor has verified demographic data at this scale. We're building the infrastructure that makes targeting possible.
            </p>

            <div className="cards-grid two-col-cards">
              <div className="card">
                <div className="card-icon">&#128100;</div>
                <h3 className="card-title">Full Audience Demographics</h3>
                <p className="card-text">Creators connect accounts — we access location, age, gender, and interests. Real data, not estimates.</p>
              </div>
              <div className="card">
                <div className="card-icon">&#127758;</div>
                <h3 className="card-title">Tier 1/Tier 2 Pricing</h3>
                <p className="card-text">Brands pay premium for verified US/UK audiences. Geographic pricing based on actual viewer data.</p>
              </div>
              <div className="card">
                <div className="card-icon">&#128279;</div>
                <h3 className="card-title">Attribution Engine</h3>
                <p className="card-text">Building toward full tracking — which creators actually drive conversions, not just views.</p>
              </div>
              <div className="card">
                <div className="card-icon">&#128274;</div>
                <h3 className="card-title">Defensible Data Moat</h3>
                <p className="card-text">Every creator connection deepens our data advantage. Network effects compound over time.</p>
              </div>
            </div>

            <div className="differentiator-box" style={{ marginTop: '32px' }}>
              <div className="differentiator-text">
                <span className="highlight">Currently building</span> — this is the layer that turns Content Rewards into a true platform
              </div>
            </div>
          </div>

          {/* Slide 14: The Ask */}
          <div className="slide ask">
            <span className="slide-label">The Ask</span>
            <h2 className="slide-title">We're Raising</h2>
            <div className="ask-amount">$15M</div>
            <div className="ask-valuation">at $120M post-money valuation</div>

            <div className="ask-details">
              <div className="ask-detail">
                <div className="ask-detail-label">Target Close</div>
                <div className="ask-detail-value">End of February 2026</div>
              </div>
              <div className="ask-detail">
                <div className="ask-detail-label">Incorporation</div>
                <div className="ask-detail-value">Delaware Corporation</div>
              </div>
              <div className="ask-detail">
                <div className="ask-detail-label">Current Status</div>
                <div className="ask-detail-value accent">Operationally Profitable</div>
              </div>
            </div>

            <div className="allocation-grid">
              <div className="allocation-item">
                <div className="allocation-percent">38.75%</div>
                <div className="allocation-label">Marketing &<br />Incentives</div>
              </div>
              <div className="allocation-item">
                <div className="allocation-percent">25%</div>
                <div className="allocation-label">Operations &<br />Infrastructure</div>
              </div>
              <div className="allocation-item">
                <div className="allocation-percent">23.75%</div>
                <div className="allocation-label">Development</div>
              </div>
              <div className="allocation-item">
                <div className="allocation-percent">10%</div>
                <div className="allocation-label">Strategic<br />Reserve</div>
              </div>
              <div className="allocation-item">
                <div className="allocation-percent">2.5%</div>
                <div className="allocation-label">Legal &<br />Admin</div>
              </div>
            </div>
          </div>

          {/* Slide 15: Summary */}
          <div className="slide">
            <span className="slide-label">Summary</span>
            <h2 className="slide-title">The Opportunity<br /><span className="highlight">At a Glance</span></h2>

            <div className="summary-grid">
              <div className="summary-card">
                <div className="summary-card-header">
                  <div className="summary-card-icon">&#127919;</div>
                  <div className="summary-card-title">The Problem</div>
                </div>
                <div className="summary-card-content">
                  <strong>$1.3B</strong> lost to fraud annually. <strong>28%</strong> of brands cite ROI as #1 challenge. Influencer marketing is opaque, slow, and unscalable.
                </div>
              </div>

              <div className="summary-card">
                <div className="summary-card-header">
                  <div className="summary-card-icon">&#128161;</div>
                  <div className="summary-card-title">The Solution</div>
                </div>
                <div className="summary-card-content">
                  First enterprise-ready platform for organic content. Transparent CPM, automated payouts, multi-layer fraud detection with <strong>sound detection</strong> (unique).
                </div>
              </div>

              <div className="summary-card">
                <div className="summary-card-header">
                  <div className="summary-card-icon">&#128200;</div>
                  <div className="summary-card-title">Traction</div>
                </div>
                <div className="summary-card-content">
                  <span className="highlight-value">105K creators</span>, <span className="highlight-value">3.7B views</span>, <span className="highlight-value">408% MoM growth</span>. <strong>~$85K monthly revenue</strong>. Zero marketing spend.
                </div>
              </div>

              <div className="summary-card">
                <div className="summary-card-header">
                  <div className="summary-card-icon">&#129309;</div>
                  <div className="summary-card-title">Strategic Edge</div>
                </div>
                <div className="summary-card-content">
                  Airrack as equity partner (<strong>50M+ followers</strong>). Whop migration absorbed <strong>$1M+/mo GMV</strong>. <strong>447 agencies</strong> onboarded.
                </div>
              </div>

              <div className="summary-card">
                <div className="summary-card-header">
                  <div className="summary-card-icon">&#127758;</div>
                  <div className="summary-card-title">Market Opportunity</div>
                </div>
                <div className="summary-card-content">
                  Creator economy: <span className="highlight-value">$250B &#8594; $480B</span> by 2027. Influencer marketing: <span className="highlight-value">$32B &#8594; $48B</span>. Building the programmatic layer.
                </div>
              </div>

              <div className="summary-card">
                <div className="summary-card-header">
                  <div className="summary-card-icon">&#128176;</div>
                  <div className="summary-card-title">The Ask</div>
                </div>
                <div className="summary-card-content">
                  <span className="highlight-value">$15M</span> at <span className="highlight-value">$120M post-money</span>. Target close: <strong>End of February 2026</strong>. Operationally profitable today.
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Controls */}
        <div className="nav-controls">
          <button
            className="nav-btn"
            onClick={prevSlide}
            disabled={isFirstSlide}
            aria-label="Previous slide"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${progress}%` }}
            />
          </div>
          <button
            className="nav-btn"
            onClick={nextSlide}
            disabled={isLastSlide}
            aria-label="Next slide"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
          <div className="keyboard-hint">
            <span className="key">&#8592;</span>
            <span className="key">&#8594;</span>
          </div>
        </div>
      </div>
    </div>
  );
}
